"use client"

import React, { useEffect, useState } from "react"
import { FiCalendar, FiMapPin, FiUpload, FiX } from "react-icons/fi"
import FormInput from "../../ui/form-input"
import CustomSelect from "../../ui/custom-select"
import FormCheckbox from "../../ui/form-checkbox"
import ModalSelect from "../../ui/select-modal"
import api from "../../libs/axios"
import toast from "react-hot-toast"
import { useLocation, useNavigate } from "react-router-dom"
import { DetailsLoader } from "../../utils/loader"
import TournamentMatchRules from "../../components/tournaments/tournament-match-rules"

// Tournament enums
export enum TournamentCategory {
    OPEN = 'open',
    CORPORATE = 'corporate',
    COMMUNITY = 'community',
    SCHOOL = 'school',
    COLLEGE = 'college',
    UNIVERSITY = 'university',
    SERIES = 'series',
    OTHER = 'other'
}

export enum TournamentStatus {
    DRAFT = "draft",
    REGISTRATION_OPEN = "registration_open",
    REGISTRATION_CLOSED = "registration_closed",
    ONGOING = "ongoing",
    COMPLETED = "completed",
    CANCELLED = "cancelled",
}

export enum BallType {
    LEATHER = "leather",
    TENNIS = "tennis",
    OTHER = "other",
}

export enum PitchType {
    ROUGH = "rough",
    CEMENT = "cement",
    TURF = "turf",
    MATTING = "matting",
    ASTROTURF = "astroturf",
}

// Interfaces
interface Tournament {
    id: string | number;
    name: string;
    description?: string;
    organizer_id: string | number;
    tournament_category: string;
    status: TournamentStatus;
    start_date: string;
    end_date?: string;
    registration_deadline?: string;
    max_teams?: string | number;
    entry_fee?: string | number;
    prize_pool?: string | number;
    location: string;
    rules?: string;
    banner_url?: string;
    tournament_logo_url?: string;
    last_batter_rule: boolean;
    pitch_type?: string;
    ball_type?: string;
    match_type?: string;
    ground_id?: string | number;
    more_teams: boolean;
    match_officials: boolean;
}

interface FormDataState {
    id: string | number;
    name: string;
    description: string;
    organizer_id: string | number;
    tournament_category: string;
    status: TournamentStatus;
    start_date: string;
    end_date: string;
    registration_deadline: string;
    max_teams: string;
    entry_fee: string;
    prize_pool: string;
    location: string;
    rules: string;
    banner_url: string;
    tournament_logo_url: string;
    last_batter_rule: boolean;
    pitch_type: string;
    ball_type: string;
    match_type: string;
    ground_id: string | number;
    more_teams: boolean;
    match_officials: boolean;
}

interface Ground {
    id: string | number;
    name: string;
    city?: string;
    pitch_type?: PitchType;
}

interface FormErrors {
    [key: string]: string | undefined;
}

interface LocationState {
    tournamentId?: string | number;
}

interface SelectOption {
    value: string | number;
    label: string;
    description?: string;
    data?: any;
}

interface Organizer {
    id: string | number;
    phone_number: string;
    details?: {
        first_name?: string;
        last_name?: string;
    };
}

interface PaginationData {
    current_page: number;
    per_page: number;
    total: number;
    total_pages: number;
    has_next_page: boolean;
    has_prev_page: boolean;
    next_page: number | null;
    prev_page: number | null;
}

interface FetchOrganizersResponse {
    data: SelectOption[];
    pagination: PaginationData;
}

interface ApiError extends Error {
    response?: {
        data?: {
            message?: string;
        };
    };
}

interface UploadResponse {
    files?: Array<{
        url: string;
    }>;
}

// Static options
const tournamentCategories: SelectOption[] = [
    { value: TournamentCategory.OPEN, label: "Open", description: "Open to all players" },
    { value: TournamentCategory.CORPORATE, label: "Corporate", description: "Corporate teams" },
    { value: TournamentCategory.COMMUNITY, label: "Community", description: "Community teams" },
    { value: TournamentCategory.SCHOOL, label: "School", description: "School teams" },
    { value: TournamentCategory.COLLEGE, label: "College", description: "College teams" },
    { value: TournamentCategory.UNIVERSITY, label: "University", description: "University teams" },
    { value: TournamentCategory.SERIES, label: "Series", description: "Series tournament" },
    { value: TournamentCategory.OTHER, label: "Other", description: "Other type of tournament" },
]

const tournamentStatuses: SelectOption[] = [
    { value: TournamentStatus.DRAFT, label: "Draft", description: "Tournament in draft mode" },
    { value: TournamentStatus.REGISTRATION_OPEN, label: "Registration Open", description: "Open for team registration" },
    { value: TournamentStatus.REGISTRATION_CLOSED, label: "Registration Closed", description: "Registration period ended" },
    { value: TournamentStatus.ONGOING, label: "Ongoing", description: "Tournament in progress" },
    { value: TournamentStatus.COMPLETED, label: "Completed", description: "Tournament finished" },
    { value: TournamentStatus.CANCELLED, label: "Cancelled", description: "Tournament cancelled" },
]

const ballTypes: SelectOption[] = [
    { value: BallType.LEATHER, label: "Leather Ball", description: "Traditional leather cricket ball" },
    { value: BallType.TENNIS, label: "Tennis Ball", description: "Tennis ball for casual play" },
    { value: BallType.OTHER, label: "Other", description: "Other type of ball" },
]

const pitchTypes: SelectOption[] = [
    { value: PitchType.ROUGH, label: "Rough Pitch", description: "Rough surface" },
    { value: PitchType.CEMENT, label: "Cement Pitch", description: "Cement surface" },
    { value: PitchType.TURF, label: "Turf Pitch", description: "Natural turf surface" },
    { value: PitchType.MATTING, label: "Matting Pitch", description: "Matting surface" },
    { value: PitchType.ASTROTURF, label: "Astroturf Pitch", description: "Artificial grass surface" },
]

const matchTypes = [
    { value: "limited_overs", label: "Limited Overs", description: "Limited overs format" },
    { value: "box_cricket", label: "Box Cricket", description: "Box cricket format" },
    { value: "pair_cricket", label: "Pair Cricket", description: "Pair cricket format" },
    { value: "test_match", label: "Test Match", description: "Test match format" },
    { value: "the_hundred", label: "The Hundred", description: "The Hundred format" },
]

// Helper functions
const convertToDatetimeLocal = (isoString: string | null | undefined): string => {
    if (!isoString) return "";

    try {
        const date = new Date(isoString);
        if (isNaN(date.getTime())) return "";

        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');

        return `${year}-${month}-${day}T${hours}:${minutes}`;
    } catch (error) {
        console.error("Error converting date:", error);
        return "";
    }
};

const safeToString = (value: unknown): string => {
    if (value === null || value === undefined) return "";
    return String(value);
};

const safeToNumber = (value: unknown): number => {
    if (value === null || value === undefined || value === "") return 0;
    const num = Number(value);
    return isNaN(num) ? 0 : num;
};

export default function UpdateTournament(): React.ReactElement {
    const location = useLocation();
    const locationState = location.state as LocationState | null;
    const tournamentId = locationState?.tournamentId;
    const navigate = useNavigate();

    // State declarations
    const [tournament, setTournament] = useState<Tournament | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [dataLoading, setDataLoading] = useState<boolean>(true);
    const [organizerLoading, setOrganizerLoading] = useState<boolean>(false);

    const [formData, setFormData] = useState<FormDataState>({
        id: "",
        name: "",
        description: "",
        organizer_id: "",
        tournament_category: "",
        status: TournamentStatus.DRAFT,
        start_date: "",
        end_date: "",
        registration_deadline: "",
        max_teams: "",
        entry_fee: "",
        prize_pool: "",
        location: "",
        rules: "",
        banner_url: "",
        tournament_logo_url: "",
        last_batter_rule: false,
        pitch_type: "",
        ball_type: "",
        match_type: "",
        ground_id: "",
        more_teams: false,
        match_officials: false,
    });

    // File upload states
    const [bannerFile, setBannerFile] = useState<File | null>(null);
    const [logoFile, setLogoFile] = useState<File | null>(null);
    const [bannerPreview, setBannerPreview] = useState<string>("");
    const [logoPreview, setLogoPreview] = useState<string>("");
    const [bannerUploading, setBannerUploading] = useState<boolean>(false);
    const [logoUploading, setLogoUploading] = useState<boolean>(false);

    const [errors, setErrors] = useState<FormErrors>({});
    const [apiError, setApiError] = useState<string | null>(null);

    // Fetch tournament details
    const fetchTournament = async (): Promise<void> => {
        if (!tournamentId) return;

        try {
            setDataLoading(true);
            const response = await api.get(`/tournaments/${tournamentId}`);
            const tournamentData = response.data.tournament as Tournament;
            setTournament(tournamentData);

            setFormData({
                id: tournamentData.id,
                name: safeToString(tournamentData.name),
                description: safeToString(tournamentData.description),
                organizer_id: safeToString(tournamentData.organizer_id),
                tournament_category: safeToString(tournamentData.tournament_category),
                status: tournamentData.status || TournamentStatus.DRAFT,
                start_date: convertToDatetimeLocal(tournamentData.start_date),
                end_date: convertToDatetimeLocal(tournamentData.end_date),
                registration_deadline: convertToDatetimeLocal(tournamentData.registration_deadline),
                max_teams: safeToString(tournamentData.max_teams),
                entry_fee: safeToString(tournamentData.entry_fee),
                prize_pool: safeToString(tournamentData.prize_pool),
                location: safeToString(tournamentData.location),
                rules: safeToString(tournamentData.rules),
                banner_url: safeToString(tournamentData.banner_url),
                tournament_logo_url: safeToString(tournamentData.tournament_logo_url),
                last_batter_rule: Boolean(tournamentData.last_batter_rule),
                pitch_type: safeToString(tournamentData.pitch_type),
                ball_type: safeToString(tournamentData.ball_type),
                match_type: safeToString(tournamentData.match_type),
                ground_id: Number(tournamentData.ground_id) || "",
                more_teams: Boolean(tournamentData.more_teams),
                match_officials: Boolean(tournamentData.match_officials),
            });

            if (tournamentData.banner_url) {
                setBannerPreview(tournamentData.banner_url);
            }
            if (tournamentData.tournament_logo_url) {
                setLogoPreview(tournamentData.tournament_logo_url);
            }
        } catch (error) {
            console.error("Fetch tournament error:", error);
            toast.error("Failed to fetch tournament details");
        } finally {
            setDataLoading(false);
        }
    };

    useEffect(() => {
        if (tournamentId) {
            fetchTournament();
        }
    }, [tournamentId]);

    const uploadFile = async (file: File, type: 'tournament_logos' | 'tournament_banners'): Promise<string> => {
        const uploadFormData = new FormData();
        uploadFormData.append('type', type);
        uploadFormData.append('files', file);

        try {
            const response = await api.post('/media/upload', uploadFormData);
            const responseData = response.data as UploadResponse;
            const uploadedFiles = responseData.files || [];

            if (!uploadedFiles.length) {
                throw new Error('Failed to upload file');
            }

            return uploadedFiles[0].url;
        } catch (error: any) {
            console.error('Error uploading file:', error);
            throw new Error(error?.response?.data?.message || 'Failed to upload file');
        }
    };

    const handleBannerFileChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
        const file = e.target.files?.[0];
        if (!file) return;

        if (!file.type.startsWith('image/')) {
            toast.error('Please select an image file');
            return;
        }

        if (file.size > 2 * 1024 * 1024) {
            toast.error('File size must be less than 2MB');
            return;
        }

        setBannerFile(file);

        const reader = new FileReader();
        reader.onload = (e: ProgressEvent<FileReader>) => {
            if (e.target?.result) {
                setBannerPreview(e.target.result as string);
            }
        };
        reader.readAsDataURL(file);
    };

    const handleLogoFileChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
        const file = e.target.files?.[0];
        if (!file) return;

        if (!file.type.startsWith('image/')) {
            toast.error('Please select an image file');
            return;
        }

        if (file.size > 2 * 1024 * 1024) {
            toast.error('File size must be less than 2MB');
            return;
        }

        setLogoFile(file);

        const reader = new FileReader();
        reader.onload = (e: ProgressEvent<FileReader>) => {
            if (e.target?.result) {
                setLogoPreview(e.target.result as string);
            }
        };
        reader.readAsDataURL(file);
    };

    const removeBanner = (): void => {
        setBannerFile(null);
        setBannerPreview("");
        setFormData(prev => ({ ...prev, banner_url: "" }));
    };

    const removeLogo = (): void => {
        setLogoFile(null);
        setLogoPreview("");
        setFormData(prev => ({ ...prev, tournament_logo_url: "" }));
    };

    const validateForm = (): boolean => {
        const newErrors: FormErrors = {};

        if (!formData.name.trim()) {
            newErrors.name = "Tournament name is required";
        } else if (formData.name.length < 3 || formData.name.length > 150) {
            newErrors.name = "Tournament name must be between 3-150 characters";
        }

        if (!formData.organizer_id) {
            newErrors.organizer_id = "Tournament organizer is required";
        }

        if (!formData.tournament_category) {
            newErrors.tournament_category = "Tournament category is required";
        }

        if (!formData.start_date) {
            newErrors.start_date = "Start date is required";
        } else {
            const startDate = new Date(formData.start_date);
            const now = new Date();
            now.setHours(0, 0, 0, 0);
            if (startDate < now) {
                newErrors.start_date = "Start date cannot be in the past";
            }
        }

        if (!formData.end_date) {
            newErrors.end_date = "End date is required"
        } else {
            const startDate = new Date(formData.start_date);
            const endDate = new Date(formData.end_date);
            if (endDate <= startDate) {
                newErrors.end_date = "End date must be after start date";
            }
        }

        if (formData.registration_deadline) {
            const regDeadline = new Date(formData.registration_deadline);
            const startDate = new Date(formData.start_date);
            if (regDeadline >= startDate) {
                newErrors.registration_deadline = "Registration deadline must be before start date";
            }

            const now = new Date();
            if (regDeadline < now) {
                newErrors.registration_deadline = "Registration deadline cannot be in the past";
            }
        }

        if (formData.max_teams) {
            const maxTeams = safeToNumber(formData.max_teams);
            if (maxTeams < 2 || maxTeams > 128) {
                newErrors.max_teams = "Max teams must be between 2 and 128";
            }
        }

        if (formData.entry_fee) {
            const entryFee = safeToNumber(formData.entry_fee);
            if (entryFee < 0) {
                newErrors.entry_fee = "Entry fee cannot be negative";
            }
        }

        if (formData.prize_pool) {
            const prizePool = safeToNumber(formData.prize_pool);
            if (prizePool < 0) {
                newErrors.prize_pool = "Prize pool cannot be negative";
            }
        }

        if (!formData.match_type) {
            newErrors.match_type = "Match type is required"
        }
        if (!formData.ball_type) {
            newErrors.ball_type = "Ball type is required"
        }

        if (!formData.pitch_type) {
            newErrors.pitch_type = "Pitch type is required"
        }

        if (!formData.ground_id) {
            newErrors.ground_id = "Ground selection is required"
        }

        if (!formData.location.trim()) {
            newErrors.location = "Location is required";
        } else if (formData.location.length > 500) {
            newErrors.location = "Location must be less than 500 characters";
        }

        if (formData.rules && formData.rules.length > 2000) {
            newErrors.rules = "Rules must be less than 2000 characters";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
        e.preventDefault();
        setApiError(null);

        if (!validateForm()) {
            const errorMessages = Object.values(errors || {}).join(", ");
            if (errorMessages) {
                toast.error(errorMessages);
            } else {
                toast.error("Please fix the errors in the form");
            }
            return;
        }

        if (!tournament) {
            toast.error("Tournament data not loaded");
            return;
        }

        setLoading(true);

        try {
            let bannerUrl = formData.banner_url;
            let logoUrl = formData.tournament_logo_url;

            if (bannerFile) {
                setBannerUploading(true);
                try {
                    bannerUrl = await uploadFile(bannerFile, 'tournament_banners');
                } catch (error) {
                    const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
                    toast.error('Failed to upload banner: ' + errorMessage);
                    return;
                } finally {
                    setBannerUploading(false);
                }
            }

            if (logoFile) {
                setLogoUploading(true);
                try {
                    logoUrl = await uploadFile(logoFile, 'tournament_logos');
                } catch (error) {
                    const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
                    toast.error('Failed to upload logo: ' + errorMessage);
                    return;
                } finally {
                    setLogoUploading(false);
                }
            }

            const payload: Record<string, unknown> = {
                name: formData.name,
                description: formData.description || null,
                organizer_id: safeToNumber(formData.organizer_id),
                tournament_category: formData.tournament_category,
                status: formData.status,
                start_date: new Date(formData.start_date).toISOString(),
                location: formData.location,
                last_batter_rule: formData.last_batter_rule,
                more_teams: formData.more_teams,
                match_officials: formData.match_officials,
            };

            if (formData.end_date) {
                payload.end_date = new Date(formData.end_date).toISOString();
            }

            if (formData.registration_deadline) {
                payload.registration_deadline = new Date(formData.registration_deadline).toISOString();
            }

            if (formData.max_teams) {
                payload.max_teams = safeToNumber(formData.max_teams);
            }

            if (formData.entry_fee) {
                payload.entry_fee = safeToNumber(formData.entry_fee);
            }

            if (formData.prize_pool) {
                payload.prize_pool = safeToNumber(formData.prize_pool);
            }

            if (formData.rules) {
                payload.rules = formData.rules;
            }

            if (bannerUrl) {
                payload.banner_url = bannerUrl;
            }

            if (logoUrl) {
                payload.tournament_logo_url = logoUrl;
            }

            if (formData.pitch_type) {
                payload.pitch_type = formData.pitch_type;
            }

            if (formData.ball_type) {
                payload.ball_type = formData.ball_type;
            }

            if (formData.match_type) {
                payload.match_type = formData.match_type;
            }

            if (formData.ground_id) {
                payload.ground_id = safeToNumber(formData.ground_id);
            }

            await api.put(`/tournaments/${tournament.id}`, payload);
            toast.success("Tournament updated successfully");
            navigate("/tournaments");
        } catch (error) {
            console.error("Error:", error);
            const apiError = error as ApiError;
            const errorMessage = apiError.response?.data?.message ||
                `Network Error: ${apiError.message || "Could not connect to the server."}`;
            setApiError(errorMessage);
            toast.error("Failed to update tournament");
        } finally {
            setLoading(false);
        }
    };

    const fetchOrganizers = async (
        page: number,
        search?: string,
        limit: number = 10,
        organizerId?: string | number
    ): Promise<FetchOrganizersResponse> => {
        try {
            const params = new URLSearchParams({
                page: page.toString(),
                limit: limit.toString(),
                ...(search ? { search } : {}),
                ...(organizerId ? { organizer_id: organizerId.toString() } : {}),
            });

            const response = await api.get(`/users/organizers?${params}`);
            const organizersData = response.data?.data || [];
            const paginationData = response.data?.pagination;

            const data: SelectOption[] = organizersData.map((org: Organizer) => {
                const firstName = org.details?.first_name || '';
                const lastName = org.details?.last_name || '';
                const fullName = `${firstName} ${lastName}`.trim();

                return {
                    value: org.id.toString(),
                    label: fullName || org.phone_number || `User ${org.id}`,
                    description: org.phone_number,
                    data: org,
                };
            });

            const pagination: PaginationData = {
                current_page: paginationData?.current_page || page,
                per_page: paginationData?.per_page || limit,
                total: paginationData?.total || 0,
                total_pages: paginationData?.total_pages || Math.ceil((paginationData?.total || 0) / limit),
                has_next_page: paginationData?.has_next_page || false,
                has_prev_page: paginationData?.has_prev_page || page > 1,
                next_page: paginationData?.next_page || null,
                prev_page: paginationData?.prev_page || null,
            };

            return { data, pagination };
        } catch (error) {
            console.error("Error fetching organizers:", error);

            return {
                data: [],
                pagination: {
                    current_page: page,
                    per_page: limit,
                    total: 0,
                    total_pages: 0,
                    has_next_page: false,
                    has_prev_page: page > 1,
                    next_page: null,
                    prev_page: page > 1 ? page - 1 : null,
                },
            };
        }
    };

    const fetchGrounds = async (page: number, search?: string, limit: number = 10, groundId?: string | number) => {
        try {
            const params = new URLSearchParams({
                page: page.toString(),
                limit: limit.toString(),
                ...(search ? { search } : {}),
                ...(groundId ? { ground_id: groundId.toString() } : {}),
            });

            const response = await api.get(`/grounds/name?${params}`);

            const groundsData = response.data?.data?.data || response.data?.data || [];
            const paginationData = response.data?.data?.pagination || response.data?.pagination;

            const data = groundsData.map((ground: any) => ({
                value: ground.id.toString(),
                label: ground.name,
                description: `${ground.city || ''}, ${ground.country || ''} • ${ground.pitch_type || ''}`.trim(),
                data: ground,
            }));

            const pagination = {
                current_page: paginationData?.current_page || page,
                per_page: paginationData?.per_page || limit,
                total: paginationData?.total || 0,
                total_pages: paginationData?.total_pages || Math.ceil((paginationData?.total || 0) / limit),
                has_next_page: paginationData?.has_next_page || false,
                has_prev_page: paginationData?.has_prev_page || page > 1,
                next_page: paginationData?.next_page || null,
                prev_page: paginationData?.prev_page || null,
            };

            return { data, pagination };
        } catch (error: any) {
            console.error("Error fetching grounds:", error);
            throw error;
        }
    };

    if (dataLoading) {
        return <DetailsLoader />;
    }

    if (!tournament) {
        return (
            <div className="min-h-screen bg-gray-50 p-6 flex items-center justify-center">
                <div className="text-center">
                    <p className="text-red-600">Failed to load tournament data</p>
                    <button
                        onClick={() => navigate("/tournaments")}
                        className="mt-4 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                    >
                        Back to Tournaments
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 p-6">
            <div className="flex items-center mb-6">
                <span className="bg-primary-700 w-1 h-6 mr-3 rounded"></span>
                <h2 className="text-xl font-bold text-primary-950 flex items-center">
                    Edit Tournament
                </h2>
            </div>

            <div className="mx-auto bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="border-b border-gray-100 pb-4 mb-6">
                    <h3 className="text-xl font-semibold text-gray-900">
                        Edit Tournament Details
                    </h3>
                    <p className="text-sm text-gray-600 mt-1">
                        You are editing tournament: <span className="font-medium text-primary-600">{tournament.name}</span>
                    </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-8">
                    {/* Basic Information */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="md:col-span-2">
                            <FormInput
                                label="Tournament Name"
                                value={formData.name}
                                onChange={(value: string) => setFormData((prev) => ({ ...prev, name: value }))}
                                placeholder="Enter tournament name"
                                error={errors.name}
                                required
                            />
                        </div>

                        <div className="md:col-span-2">
                            <FormInput
                                label="Description"
                                value={formData.description || ""}
                                onChange={(value: string) => setFormData((prev) => ({ ...prev, description: value }))}
                                placeholder="Enter tournament description"
                            />
                        </div>

                        <ModalSelect
                            label="Select Organizer"
                            placeholder="Choose an organizer"
                            value={formData.organizer_id}
                            onChange={(option: SelectOption) => {
                                setFormData((prev) => ({ ...prev, organizer_id: option.value }));
                            }}
                            fetchOptions={(page, search, limit) =>
                                fetchOrganizers(page, search, limit, formData?.organizer_id)
                            }
                            required
                            searchPlaceholder="Search by name or phone..."
                            error={errors.organizer_id}
                            disabled={organizerLoading}
                            renderOption={(option) => (
                                <div>
                                    <div className="font-medium text-gray-900">{option.label}</div>
                                    <div className="text-sm text-gray-500 capitalize flex items-center mt-0.5">
                                        {option.description}
                                    </div>
                                </div>
                            )}
                        />

                        <CustomSelect
                            label="Tournament Category"
                            options={tournamentCategories}
                            value={formData.tournament_category}
                            onChange={(value: string | number) => setFormData((prev) => ({ ...prev, tournament_category: value as string }))}
                            placeholder="Select category"
                            searchable
                        />

                        <CustomSelect
                            label="Tournament Status"
                            options={tournamentStatuses}
                            value={formData.status}
                            onChange={(value: string | number) => setFormData((prev) => ({ ...prev, status: value as TournamentStatus }))}
                            placeholder="Select status"
                            searchable
                        />

                        <ModalSelect
                            label="Ground Name"
                            placeholder="Select a ground"
                            value={formData.ground_id}
                            onChange={(option) => {
                                const selectedGround = option.data;
                                setFormData(prev => ({
                                    ...prev,
                                    ground_id: option.value,
                                    location: selectedGround?.city || '',
                                    pitch_type: selectedGround?.pitch_type || ''
                                }));
                            }}
                            fetchOptions={(page, search, limit) =>
                                fetchGrounds(page, search, limit, formData?.ground_id)
                            }
                            required
                            searchPlaceholder="Search grounds..."
                            error={errors.ground_id}
                            renderOption={(option) => (
                                <div>
                                    <div className="font-medium text-gray-900">{option.label}</div>
                                    <div className="text-sm text-gray-500 flex items-center mt-0.5">
                                        {option.description}
                                    </div>
                                </div>
                            )}
                        />

                        <div className="relative">
                            <FormInput
                                label="Location"
                                value={formData.location}
                                onChange={(value: string) => setFormData((prev) => ({ ...prev, location: value }))}
                                placeholder="Enter tournament location"
                                error={errors.location}
                                required
                            />
                            <FiMapPin className="absolute right-3 top-9 text-gray-400 w-5 h-5 pointer-events-none" />
                        </div>
                    </div>

                    {/* Date Information */}
                    <div className="border-t border-gray-100 pt-6">
                        <h4 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                            <FiCalendar className="w-5 h-5 mr-2 text-primary-600" />
                            Tournament Dates
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <div className="relative">
                                <FormInput
                                    label="Start Date"
                                    type="datetime-local"
                                    value={formData.start_date}
                                    onChange={(value: string) => setFormData((prev) => ({ ...prev, start_date: value }))}
                                    error={errors.start_date}
                                    required
                                />
                                <FiCalendar className="absolute right-3 top-9 text-gray-400 w-5 h-5 pointer-events-none" />
                            </div>

                            <div className="relative">
                                <FormInput
                                    label="End Date"
                                    type="datetime-local"
                                    value={formData.end_date}
                                    onChange={(value: string) => setFormData((prev) => ({ ...prev, end_date: value }))}
                                    required
                                    error={errors.end_date}
                                    min={formData.start_date || undefined}
                                />
                                <FiCalendar className="absolute right-3 top-9 text-gray-400 w-5 h-5 pointer-events-none" />
                            </div>

                            <div className="relative">
                                <FormInput
                                    label="Registration Deadline"
                                    type="datetime-local"
                                    value={formData.registration_deadline}
                                    onChange={(value: string) => setFormData((prev) => ({ ...prev, registration_deadline: value }))}
                                    error={errors.registration_deadline}
                                    max={formData.start_date || undefined}
                                />
                                <FiCalendar className="absolute right-3 top-9 text-gray-400 w-5 h-5 pointer-events-none" />
                            </div>
                        </div>
                    </div>

                    {/* Tournament Configuration */}
                    <div className="border-t border-gray-100 pt-6">
                        <h4 className="text-lg font-medium text-gray-900 mb-4">Tournament Configuration</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <CustomSelect
                                label="Match Type"
                                options={matchTypes}
                                value={formData.match_type || ""}
                                onChange={(value: string | number) => setFormData((prev) => ({ ...prev, match_type: value as string }))}
                                placeholder="Select match type"
                                required
                                error={errors.match_type}
                                searchable
                            />

                            <CustomSelect
                                label="Ball Type"
                                options={ballTypes}
                                value={formData.ball_type || ""}
                                onChange={(value: string | number) => setFormData((prev) => ({ ...prev, ball_type: value as string }))}
                                placeholder="Select ball type"
                                required
                                error={errors.ball_type}
                                searchable
                            />

                            <CustomSelect
                                label="Pitch Type"
                                options={pitchTypes}
                                value={formData.pitch_type || ""}
                                onChange={(value: string | number) => setFormData((prev) => ({ ...prev, pitch_type: value as string }))}
                                placeholder="Select pitch type"
                                searchable
                                required
                                error={errors.pitch_type}
                            />
                        </div>
                    </div>

                    {/* Media and Additional Information */}
                    <div className="border-t border-gray-100 pt-6">
                        <h4 className="text-lg font-medium text-gray-900 mb-4">Media & Additional Details</h4>

                        {/* Banner and Logo Upload */}
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-6">
                            {/* Banner Upload */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Tournament Banner
                                    <span className="text-xs text-gray-500 ml-1">(Recommended: 16:9 ratio)</span>
                                </label>
                                <div className="space-y-3">
                                    {bannerPreview ? (
                                        <div className="relative group">
                                            <img
                                                src={bannerPreview}
                                                alt="Banner preview"
                                                className="w-full h-32 object-cover rounded-lg border border-gray-200 shadow-sm"
                                            />
                                            <button
                                                type="button"
                                                onClick={removeBanner}
                                                className="absolute top-2 right-2 bg-red-500 text-white p-1.5 rounded-full hover:bg-red-600 opacity-0 group-hover:opacity-100 transition-opacity duration-200 shadow-md"
                                            >
                                                <FiX className="w-4 h-4" />
                                            </button>
                                        </div>
                                    ) : (
                                        <div className="w-full h-32 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center bg-gray-50 hover:bg-gray-100 transition-colors duration-200">
                                            <div className="text-center">
                                                <FiUpload className="w-6 h-6 text-gray-400 mx-auto mb-2" />
                                                <span className="text-sm text-gray-500">Preview banner</span>
                                            </div>
                                        </div>
                                    )}
                                    <div className="flex items-center space-x-2">
                                        <input
                                            type="file"
                                            accept="image/*"
                                            onChange={handleBannerFileChange}
                                            className="hidden"
                                            id="banner-upload"
                                        />
                                        <label
                                            htmlFor="banner-upload"
                                            className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-md cursor-pointer hover:bg-gray-50 transition-colors duration-200 text-sm"
                                        >
                                            <FiUpload className="w-4 h-4" />
                                            <span>Choose Banner</span>
                                        </label>
                                        {bannerUploading && (
                                            <span className="text-sm text-blue-600 flex items-center">
                                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                                                Uploading...
                                            </span>
                                        )}
                                    </div>
                                    <p className="text-xs text-gray-500">Maximum file size: 2MB. Supported formats: JPG, PNG, WEBP</p>
                                </div>
                            </div>

                            {/* Logo Upload - Square Shape */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Tournament Logo
                                    <span className="text-xs text-gray-500 ml-1">(Square recommended)</span>
                                </label>
                                <div className="space-y-3">
                                    {logoPreview ? (
                                        <div className="relative group">
                                            <div className="w-32 h-32">
                                                <img
                                                    src={logoPreview}
                                                    alt="Logo preview"
                                                    className="w-full h-full object-cover rounded-lg border border-gray-200 shadow-sm"
                                                />
                                            </div>
                                            <button
                                                type="button"
                                                onClick={removeLogo}
                                                className="absolute top-2 left-24 bg-red-500 text-white p-1.5 rounded-full hover:bg-red-600 opacity-0 group-hover:opacity-100 transition-opacity duration-200 shadow-md"
                                            >
                                                <FiX className="w-4 h-4" />
                                            </button>
                                        </div>
                                    ) : (
                                        <div className="w-32 h-32 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center bg-gray-50 hover:bg-gray-100 transition-colors duration-200">
                                            <div className="text-center">
                                                <FiUpload className="w-6 h-6 text-gray-400 mx-auto mb-2" />
                                                <span className="text-xs text-gray-500">Preview logo</span>
                                            </div>
                                        </div>
                                    )}
                                    <div className="flex items-center">
                                        <input
                                            type="file"
                                            accept="image/*"
                                            onChange={handleLogoFileChange}
                                            className="hidden"
                                            id="logo-upload"
                                        />
                                        <label
                                            htmlFor="logo-upload"
                                            className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-md cursor-pointer hover:bg-gray-50 transition-colors duration-200 text-sm"
                                        >
                                            <FiUpload className="w-4 h-4" />
                                            <span>Choose Logo</span>
                                        </label>
                                        {logoUploading && (
                                            <span className="text-sm text-blue-600 flex items-center">
                                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                                                Uploading...
                                            </span>
                                        )}
                                    </div>
                                    <p className="text-xs text-gray-500 text-left">Maximum file size: 2MB. Supported formats: JPG, PNG, WEBP</p>
                                </div>
                            </div>
                        </div>

                        <div className="md:col-span-2">
                            <FormInput
                                label="Rules & Regulations"
                                value={formData.rules || ""}
                                onChange={(value: string) => setFormData((prev) => ({ ...prev, rules: value }))}
                                placeholder="Enter tournament rules and regulations (optional)"
                                error={errors.rules}
                            />
                        </div>
                    </div>

                    {/* Tournament Settings */}
                    <div className="border-t border-gray-100 pt-6">
                        <h4 className="text-lg font-medium text-gray-900 mb-4">Tournament Settings</h4>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                                <FormCheckbox
                                    label="Last Batter Rule"
                                    checked={formData.last_batter_rule}
                                    onChange={(checked: boolean) => setFormData((prev) => ({ ...prev, last_batter_rule: checked }))}
                                    description="Enable special last batter rule for this tournament"
                                />
                            </div>

                            <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                                <FormCheckbox
                                    label="Allow More Teams"
                                    checked={formData.more_teams}
                                    onChange={(checked: boolean) => setFormData((prev) => ({ ...prev, more_teams: checked }))}
                                    description="Allow registration beyond the maximum teams limit"
                                />
                            </div>

                            <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                                <FormCheckbox
                                    label="Match Officials Required"
                                    checked={formData.match_officials}
                                    onChange={(checked: boolean) => setFormData((prev) => ({ ...prev, match_officials: checked }))}
                                    description="Require professional match officials for games"
                                />
                            </div>
                        </div>
                    </div>

                    {apiError && (
                        <div className="p-4 bg-red-50 border border-red-200 text-red-800 rounded-lg text-sm flex items-start space-x-2">
                            <div className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0">⚠️</div>
                            <div>{apiError}</div>
                        </div>
                    )}

                    <div className="flex justify-end pt-6 border-t border-gray-100 space-x-4">
                        <button
                            type="button"
                            onClick={() => navigate("/tournaments")}
                            className="px-6 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-colors duration-200 font-medium"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={loading || bannerUploading || logoUploading}
                            className="px-6 py-2.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 font-medium min-w-[160px]"
                        >
                            {loading ? (
                                <span className="flex items-center justify-center">
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                    Updating...
                                </span>
                            ) : bannerUploading || logoUploading ? (
                                <span className="flex items-center justify-center">
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                    Uploading...
                                </span>
                            ) : (
                                "Update Tournament"
                            )}
                        </button>
                    </div>
                </form>

                {/* DECISION-007 B3: the tournament's no-ball and wide runs, saved on their own. */}
                {tournament?.id != null && (
                    <TournamentMatchRules
                        tournamentId={tournament.id}
                        initialWideRuns={(tournament as any)?.wide_runs ?? null}
                        initialNoBallRuns={(tournament as any)?.no_ball_runs ?? null}
                    />
                )}
            </div>
        </div>
    );
}